/**
 * MM:SS for under-an-hour durations, H:MM:SS for over. Used in the parent
 * video viewer to label playback length without ever showing zero-padded
 * leading hours for short clips.
 */
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds == null || !Number.isFinite(seconds) || seconds < 0) return '—';
  const total = Math.floor(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const ss = s.toString().padStart(2, '0');
  if (h === 0) {
    return `${m}:${ss}`;
  }
  return `${h}:${m.toString().padStart(2, '0')}:${ss}`;
}

/**
 * Human-readable byte count. Uses SI units (1000-based) — admin sees
 * "120 MB", not "114.4 MiB", which matches what most cloud-storage UIs do.
 */
export function formatBytes(bytes: number | null | undefined): string {
  if (bytes == null || !Number.isFinite(bytes) || bytes <= 0) return '—';
  const units = ['B', 'KB', 'MB', 'GB'] as const;
  let i = 0;
  let v = bytes;
  while (v >= 1000 && i < units.length - 1) {
    v /= 1000;
    i += 1;
  }
  // Show 1 decimal place for KB+, 0 for raw bytes.
  return `${i === 0 ? v.toFixed(0) : v.toFixed(1)} ${units[i]}`;
}

/**
 * Compute a SHA-256 hex digest of an entire File / Blob. Used at upload
 * time so we can record an integrity checksum the back end stores on
 * media_assets.checksum_sha256. Hash is computed in 4 MB chunks so we
 * don't blow out memory on a 500 MB video.
 */
export async function sha256OfBlob(blob: Blob): Promise<string> {
  // Streaming SHA-256 via SubtleCrypto isn't directly supported (it requires
  // the full buffer). For our size cap (500 MB), buffering is acceptable on
  // modern browsers — the wall-clock cost is dominated by the upload itself.
  const buf = await blob.arrayBuffer();
  const hashBuf = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(hashBuf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Detect the duration of a video file by playing it briefly in a hidden
 * <video> element and reading `duration`. Resolves null on failure so the
 * upload flow can still complete without a duration (admin can finalize
 * without it; the field is optional on the back end).
 */
export function probeVideoDuration(blob: Blob): Promise<number | null> {
  if (typeof document === 'undefined') return Promise.resolve(null);
  return new Promise((resolve) => {
    const url = URL.createObjectURL(blob);
    const v = document.createElement('video');
    v.preload = 'metadata';
    v.muted = true;
    let settled = false;
    const cleanup = () => {
      URL.revokeObjectURL(url);
      v.remove();
    };
    const finish = (val: number | null) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(val);
    };
    v.onloadedmetadata = () => {
      if (Number.isFinite(v.duration) && v.duration > 0) {
        finish(Math.round(v.duration));
      } else {
        finish(null);
      }
    };
    v.onerror = () => finish(null);
    // Failsafe timeout — some codecs never resolve metadata.
    setTimeout(() => finish(null), 5000);
    v.src = url;
  });
}
